import { useState } from 'react';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { useDeleteNudge, useSalesNudges, useSetNudgeStatus } from '@/hooks/use-sales-nudges';
import { newNudgePanelState } from '../nudge-draft';
import type { Nudge } from '../types';
import { NudgePanel, type NudgePanelState } from './nudge-panel';
import { NudgesTable } from './nudges-table';
import { NudgesEmptyState } from './nudges-empty-state';

interface NudgesTabProps {
	panel: NudgePanelState | null;
	onPanelChange: (panel: NudgePanelState | null) => void;
}

const NudgesSkeleton = () => (
	<div className='flex flex-col'>
		<div className='flex h-10 items-center border-b border-gray-300 px-4'>
			<Skeleton className='h-3 w-12' />
		</div>
		{[1, 2, 3].map((row) => (
			<div key={row} className='mx-4 flex h-12 items-center justify-between border-b border-gray-300'>
				<Skeleton className='h-3 w-40' />
				<Skeleton className='h-6 w-14 rounded-full' />
			</div>
		))}
	</div>
);

/**
 * The Nudges tab: the list, its toggles and menu, and the side panel.
 *
 * The panel state lives with the page because "Create Nudge" sits in the page header.
 */
export const NudgesTab = ({ panel, onPanelChange }: NudgesTabProps) => {
	const { data: nudges = [], isLoading, error } = useSalesNudges();
	const setStatusMutation = useSetNudgeStatus();
	const deleteMutation = useDeleteNudge();
	const [nudgeToDelete, setNudgeToDelete] = useState<Nudge | null>(null);

	/**
	 * No confirm on either direction: the switch is instantly reversible. Enabling a nudge with no
	 * "What to nudge" is refused by the server, and its message is what the toast shows.
	 */
	const toggle = (nudge: Nudge) => {
		if (setStatusMutation.isPending) return;
		const status = nudge.status === 'enabled' ? 'disabled' : 'enabled';
		setStatusMutation.mutate(
			{ nudgeId: nudge.id, status },
			{
				onError: (err) =>
					toast.error(getApiErrorMessage(err, `Could not ${status === 'enabled' ? 'enable' : 'disable'} ${nudge.name}`))
			}
		);
	};

	const confirmDelete = () => {
		if (!nudgeToDelete || deleteMutation.isPending) return;
		const { id, name } = nudgeToDelete;
		deleteMutation.mutate(id, {
			onSuccess: () => {
				toast.success(`Deleted ${name}`);
				if (panel?.nudge?.id === id) onPanelChange(null);
			},
			onError: (err) => toast.error(getApiErrorMessage(err, `Could not delete ${name}`)),
			onSettled: () => setNudgeToDelete(null)
		});
	};

	const pendingNudgeId = setStatusMutation.isPending ? (setStatusMutation.variables?.nudgeId ?? null) : null;

	return (
		<div className='flex-1 overflow-auto'>
			{isLoading && <NudgesSkeleton />}

			{error && (
				<p className='px-4 py-8 text-center text-sm text-destructive'>
					{getApiErrorMessage(error, 'Could not load nudges. Please try again.')}
				</p>
			)}

			{!isLoading &&
				!error &&
				(nudges.length === 0 ? (
					<NudgesEmptyState onCreate={() => onPanelChange(newNudgePanelState(nudges))} />
				) : (
					<NudgesTable
						nudges={nudges}
						pendingNudgeId={pendingNudgeId}
						onOpen={(nudge) => onPanelChange({ mode: 'view', nudge })}
						onEdit={(nudge) => onPanelChange({ mode: 'edit', nudge })}
						onToggle={toggle}
						onDelete={setNudgeToDelete}
					/>
				))}

			<NudgePanel state={panel} onStateChange={onPanelChange} />

			<ConfirmDialog
				open={nudgeToDelete !== null}
				title='Delete nudge?'
				description='Deleting this nudge is permanent. Do you want to continue?'
				confirmLabel='Delete'
				destructive
				isPending={deleteMutation.isPending}
				onConfirm={confirmDelete}
				onCancel={() => setNudgeToDelete(null)}
			/>
		</div>
	);
};
